import { useCallback, useMemo } from "react";

import {
  activeBusinessFiltersState,
  advancedSearchCriteriasSelector,
  buildAdvancedFilterList,
  BusinessFilter,
  BusinessFilterNature,
  businessFilterPanelIsOpenState,
  createAdvancedBusinessFilter,
  createAdvancedThesaurusBusinessFilter,
  createBasketBusinessFilter,
  createFolderBusinessFilter,
  createFullTextBusinessFilter,
  currentDataLocaleSelector,
  DataViewKey,
  getBusinessFilterFirstValue,
  getBusinessFiltersForCurrentLocale,
  getKpkMetaFieldLabelByFieldName,
  KpkFilterType,
  MediaField,
  SentryBreadcrumbCategory,
  sheetFieldsSelector,
  useStatsQueueSender,
} from "@keepeek/commons";
import { KSplitValue } from "@keepeek/refront-components";
import { Theme, useMediaQuery } from "@mui/material";
import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from "recoil";

import { CONFIGURATION_SECTION_PATH } from "../../components/layout/adminMenu";
import { PagesPath } from "../../containers/App/utils";
import logger from "../../lib/logger-utils";
import { Autocomplete } from "../../models/configuration/components/autocomplete";
import { useActiveFiltersFromURL } from "../businessFilter/hooks/useActiveFiltersFromURL";
import { useConfiguration } from "../config/hooks";
import { useInitConfig } from "../config/hooks/init";
import { kSplitValueSelector } from "./selector";
import { getKSplitValueTranslation } from "./utils";

export type UseSearchInputProps = {
  fields?: string[];
  widgetId: string;
  dataViewKey: DataViewKey;
};

export const fieldIdText: string = "text";

export type UseSearchInput = {
  /**
   * Current split value (optional for state management)
   */
  kSplitValue?: KSplitValue;
  /**
   * Set current split value
   */
  setKSplitValue: (kSplitValue: KSplitValue) => void;
  /**
   * Split values
   */
  kSplitValues: KSplitValue[];
  /**
   * Perform search depending of current split value
   */
  onSearch: (query: string, splitValue: KSplitValue) => void;
};

export function useSearchInput({
  fields,
  widgetId,
  dataViewKey,
}: UseSearchInputProps): UseSearchInput {
  const { t } = useTranslation();
  const dataLocale = useRecoilValue(currentDataLocaleSelector);
  const { pathname } = useRouter();
  const autocompleteConfig = useConfiguration<Autocomplete>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_AUTOCOMPLETE,
  );

  const sheetFields = useRecoilValueLoadable(sheetFieldsSelector);
  const criterias = useRecoilValueLoadable(advancedSearchCriteriasSelector);
  const [kSplitValueWithoutTranslation, setKSplitValueWithoutTranslation] = useRecoilState(
    kSplitValueSelector(widgetId),
  );
  const kSplitValue: KSplitValue = getKSplitValueTranslation(kSplitValueWithoutTranslation, t);
  const isAboveSm = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

  const kSplitValues: KSplitValue[] | undefined = useMemo(() => {
    if (sheetFields.state === "hasValue" && criterias.state === "hasValue") {
      if (fields) {
        const values: KSplitValue[] | undefined = [];
        for (const field of fields) {
          // TODO: this is not generic, this come from filters
          const filterList: BusinessFilter[] = buildAdvancedFilterList(
            field,
            sheetFields.contents,
            criterias.contents || [],
          );
          if (!filterList || filterList.length === 0) {
            logger.debug(`[useSearchInput] can't find filter for field`, field);
            continue;
          }
          // TODO: this is not generic, this come from filters
          const filterListForCurrentLocale = getBusinessFiltersForCurrentLocale(
            filterList,
            dataLocale,
          );
          if (!filterListForCurrentLocale || filterListForCurrentLocale.length === 0) {
            logger.debug(
              `[useSearchInput] can't find filter for field and locale`,
              field,
              dataLocale,
            );
            continue;
          }
          values.push({
            id: filterListForCurrentLocale[0].filter.id,
            label: t("search.input.split.item.label", {
              field: filterListForCurrentLocale[0].filter.label,
            }).toUpperCase(),
            type: filterListForCurrentLocale[0].filter.type,
          });
        }
        if (values.length > 0) {
          // Text search first only if there are custom fields, otherwise, no split button
          return [
            {
              id: fieldIdText,
              label: t("search.input.split.item.text.label").toUpperCase(),
              type: KpkFilterType.Text,
            },
            ...values,
          ];
        }
      }
    }
    return [];
  }, [
    criterias.contents,
    criterias.state,
    dataLocale,
    sheetFields.contents,
    sheetFields.state,
    t,
    fields,
  ]);

  const { initConfig } = useInitConfig();
  const { sendTextSearchHitToQueue } = useStatsQueueSender(initConfig?.clientIp);
  const { activeFilters, setActiveFilters } = useActiveFiltersFromURL();
  const getActiveBusinessFilters = useRecoilValue(activeBusinessFiltersState(dataViewKey));
  const setBusinessFilterPanelOpenState = useSetRecoilState(businessFilterPanelIsOpenState);

  const _handleSetActiveFilters = useCallback(
    (filter: BusinessFilter) => {
      const filterFolder = getBusinessFilterFirstValue(
        activeFilters,
        BusinessFilterNature.ADVANCED,
        MediaField.FOLDER_ID,
      );
      const filterBasket = getBusinessFilterFirstValue(
        getActiveBusinessFilters,
        BusinessFilterNature.BASKET,
        BusinessFilterNature.BASKET,
      );
      if (filterFolder && pathname.includes(PagesPath.SEARCH_PAGE)) {
        return setActiveFilters([
          createFolderBusinessFilter(
            filterFolder.id,
            filterFolder.label,
            t("business-filter.advanced.folder.label"),
          ),
          filter,
        ]);
      }
      if (filterBasket && pathname.includes(PagesPath.BASKET_ELEMENT_PAGE)) {
        return setActiveFilters([createBasketBusinessFilter(filterBasket.id), filter]);
      }
      return setActiveFilters([filter]);
    },
    [activeFilters, getActiveBusinessFilters, pathname, setActiveFilters, t],
  );

  const onSearch = useCallback(
    (query: string, splitValue: KSplitValue) => {
      Sentry.addBreadcrumb({
        category: SentryBreadcrumbCategory.USER_INTERACTION,
        message: `useSearchInput - onSearch query: ${query}, splitValue: ${JSON.stringify(
          splitValue,
        )}`,
      });
      sendTextSearchHitToQueue({ searchValue: query });
      if (!splitValue || splitValue.id === fieldIdText) {
        _handleSetActiveFilters(
          createFullTextBusinessFilter(query, t("business-filter.full-text.label")),
        );
      } else {
        if (splitValue.type === KpkFilterType.Thesaurus) {
          _handleSetActiveFilters(
            createAdvancedThesaurusBusinessFilter({
              fieldId: splitValue.id,
              query,
              filterLabel: getKpkMetaFieldLabelByFieldName(
                sheetFields.state === "hasValue" ? sheetFields.contents : [],
                splitValue.id,
              ),
              searchSeparator: autocompleteConfig?.searchSeparator,
              enableNotFoundReference: true,
            }),
          );
        } else {
          _handleSetActiveFilters(
            createAdvancedBusinessFilter({
              fieldId: splitValue.id,
              query,
              filterLabel: getKpkMetaFieldLabelByFieldName(
                sheetFields.state === "hasValue" ? sheetFields.contents : [],
                splitValue.id,
              ),
              searchSeparator: autocompleteConfig?.searchSeparator,
              enableNotFoundReference: true,
            }),
          );
        }
      }
      if (isAboveSm) {
        setBusinessFilterPanelOpenState(true);
      }
    },
    [
      _handleSetActiveFilters,
      autocompleteConfig?.searchSeparator,
      isAboveSm,
      sendTextSearchHitToQueue,
      setBusinessFilterPanelOpenState,
      sheetFields.contents,
      sheetFields.state,
      t,
    ],
  );

  return {
    kSplitValue,
    setKSplitValue: setKSplitValueWithoutTranslation,
    kSplitValues,
    onSearch,
  };
}
